/**
 * Canonical Content item kind identity.
 *
 * LibraryKind / ShareContentType / plan JSONB slots / Collection tabs /
 * playback class / CMS aliases all read from this table. Adding a kind is
 * one row here plus ingest routing (which collection to fetch). Do not add
 * parallel enums in callers.
 */
export const LIBRARY_KINDS = [
    "Reset",
    "Recipe",
    "Article",
    "Story",
    "RoleModel",
    "PodcastEpisode",
    "Guide",
    "Meditation",
    "Soundscape",
    "BedtimeStory",
];
export const SHARE_CONTENT_TYPES = LIBRARY_KINDS;
export const PLAN_ITEM_KINDS = [
    "Microstep",
    "Reset",
    "Recipe",
    "Article",
    "Story",
    "RoleModel",
    "PodcastEpisode",
    "Guide",
];
/** Indexed in the Content index but not a Collection or Share kind (the show). */
export const INDEX_ONLY_KINDS = ["Podcast"];
export const COLLECTION_TAB_ORDER = [
    "article",
    "roleModel",
    "recipe",
    "reset",
    "guide",
    "podcast",
    "meditation",
    "soundscape",
    "bedtimeStory",
];
const KIND_RECORDS = {
    Reset: {
        playback: "video",
        collectionTab: "reset",
        planToday: true,
        share: true,
        planJsonbSlot: "reset",
        displayKind: "Reset",
        accessPolicyFamily: "Reset",
        titleLabel: "Reset",
        sentenceLabel: "reset",
        article: "a",
        tabTitle: "Resets",
        openCta: "Take the Reset",
        reminderKind: "reset",
        recommended: true,
    },
    Recipe: {
        playback: "read",
        collectionTab: "recipe",
        planToday: true,
        share: true,
        planJsonbSlot: "recipe",
        displayKind: "Recipe",
        accessPolicyFamily: "Recipe",
        titleLabel: "Recipe",
        sentenceLabel: "recipe",
        article: "a",
        tabTitle: "Recipes",
        openCta: "See the recipe",
        reminderKind: "recipe",
        recommended: true,
    },
    Article: {
        playback: "read",
        collectionTab: "article",
        planToday: true,
        share: true,
        planJsonbSlot: "story",
        displayKind: "Article",
        accessPolicyFamily: "Story",
        titleLabel: "Article",
        sentenceLabel: "article",
        article: "an",
        tabTitle: "Articles",
        openCta: "Read it",
        reminderKind: "article",
        recommended: true,
    },
    Story: {
        playback: "read",
        collectionTab: "article",
        planToday: true,
        share: true,
        planJsonbSlot: "story",
        displayKind: "Article",
        accessPolicyFamily: "Story",
        titleLabel: "Article",
        sentenceLabel: "story",
        article: "a",
        tabTitle: "Articles",
        openCta: "Read it",
        reminderKind: "article",
        recommended: true,
    },
    RoleModel: {
        playback: "read",
        collectionTab: "roleModel",
        planToday: true,
        share: true,
        planJsonbSlot: "roleModel",
        displayKind: "RoleModel",
        accessPolicyFamily: "Story",
        titleLabel: "Role model story",
        sentenceLabel: "role model story",
        article: "a",
        tabTitle: "Role model",
        openCta: "Read it",
        reminderKind: "article",
        recommended: true,
    },
    PodcastEpisode: {
        playback: "audio",
        collectionTab: "podcast",
        planToday: false,
        share: true,
        planJsonbSlot: "podcastEpisode",
        displayKind: "PodcastEpisode",
        accessPolicyFamily: "Story",
        titleLabel: "Podcast",
        sentenceLabel: "podcast",
        article: "a",
        tabTitle: "Podcasts",
        openCta: "Listen",
        reminderKind: null,
        recommended: true,
    },
    Guide: {
        playback: "video",
        collectionTab: "guide",
        planToday: true,
        share: true,
        planJsonbSlot: "guide",
        displayKind: "Guide",
        accessPolicyFamily: "Story",
        titleLabel: "Guide",
        sentenceLabel: "guide",
        article: "a",
        tabTitle: "Guides",
        openCta: "Watch the Guide",
        reminderKind: "reset",
        recommended: true,
    },
    // Guided meditations: portrait video on Cloudflare Stream (cms-meditations).
    Meditation: {
        playback: "video",
        collectionTab: "meditation",
        planToday: false,
        share: true,
        planJsonbSlot: null,
        displayKind: "Meditation",
        accessPolicyFamily: "Story",
        titleLabel: "Meditation",
        sentenceLabel: "meditation",
        article: "a",
        tabTitle: "Meditations",
        openCta: "Start the meditation",
        reminderKind: null,
        recommended: false,
    },
    // Ambient soundscapes: voiceless audio loops (cms-soundscapes).
    Soundscape: {
        playback: "audio",
        collectionTab: "soundscape",
        planToday: false,
        share: true,
        planJsonbSlot: null,
        displayKind: "Soundscape",
        accessPolicyFamily: "Story",
        titleLabel: "Soundscape",
        sentenceLabel: "soundscape",
        article: "a",
        tabTitle: "Soundscapes",
        openCta: "Listen",
        reminderKind: null,
        recommended: false,
    },
    // Narrated sleep stories: audio (cms-bedtime-stories).
    BedtimeStory: {
        playback: "audio",
        collectionTab: "bedtimeStory",
        planToday: false,
        share: true,
        planJsonbSlot: null,
        displayKind: "BedtimeStory",
        accessPolicyFamily: "Story",
        titleLabel: "Bedtime story",
        sentenceLabel: "bedtime story",
        article: "a",
        tabTitle: "Bedtime stories",
        openCta: "Listen",
        reminderKind: null,
        recommended: false,
    },
};
const CMS_INDEX_ALIASES = {
    recipe: "Recipe",
    recipes: "Recipe",
    article: "Article",
    articles: "Article",
    "cms-article": "Article",
    "cms-articles": "Article",
    story: "Story",
    stories: "Story",
    "cms-story": "Story",
    "cms-stories": "Story",
    rolemodel: "RoleModel",
    rolemodels: "RoleModel",
    "role-model": "RoleModel",
    "role-models": "RoleModel",
    role_model: "RoleModel",
    role_models: "RoleModel",
    "cms-role-model": "RoleModel",
    "cms-role-models": "RoleModel",
    reset: "Reset",
    resets: "Reset",
    "cms-reset": "Reset",
    "cms-resets": "Reset",
    "cms-recipe": "Recipe",
    "cms-recipes": "Recipe",
    podcast: "Podcast",
    podcasts: "Podcast",
    "cms-podcast": "Podcast",
    "cms-podcasts": "Podcast",
    "podcast-episode": "PodcastEpisode",
    podcast_episode: "PodcastEpisode",
    podcastepisode: "PodcastEpisode",
    "cms-podcast-episode": "PodcastEpisode",
    "cms-podcast-episodes": "PodcastEpisode",
    episodes: "PodcastEpisode",
    journeydailystep: "Guide",
    "journey-daily-step": "Guide",
    "journey-dailystep": "Guide",
    journey_daily_step: "Guide",
    "cms-journey-daily-step": "Guide",
    "cms-journey-daily-steps": "Guide",
    guide: "Guide",
    guides: "Guide",
    digest: "Guide",
    dailydigest: "Guide",
    "daily-digest": "Guide",
    daily_digest: "Guide",
    meditation: "Meditation",
    meditations: "Meditation",
    "cms-meditation": "Meditation",
    "cms-meditations": "Meditation",
    soundscape: "Soundscape",
    soundscapes: "Soundscape",
    "cms-soundscape": "Soundscape",
    "cms-soundscapes": "Soundscape",
    bedtimestory: "BedtimeStory",
    bedtimestories: "BedtimeStory",
    "bedtime-story": "BedtimeStory",
    "bedtime-stories": "BedtimeStory",
    bedtime_story: "BedtimeStory",
    "cms-bedtime-story": "BedtimeStory",
    "cms-bedtime-stories": "BedtimeStory",
};
/** Member-facing aliases (notifications, query strings, share). `podcast` is the episode. */
const MEMBER_KIND_ALIASES = {
    recipe: "Recipe",
    recipes: "Recipe",
    article: "Article",
    articles: "Article",
    story: "Story",
    stories: "Story",
    rolemodel: "RoleModel",
    rolemodels: "RoleModel",
    "role-model": "RoleModel",
    "role-models": "RoleModel",
    role_model: "RoleModel",
    role_models: "RoleModel",
    "role model": "RoleModel",
    reset: "Reset",
    resets: "Reset",
    podcast: "PodcastEpisode",
    podcasts: "PodcastEpisode",
    podcastepisode: "PodcastEpisode",
    "podcast-episode": "PodcastEpisode",
    podcast_episode: "PodcastEpisode",
    guide: "Guide",
    guides: "Guide",
    digest: "Guide",
    dailydigest: "Guide",
    "daily-digest": "Guide",
    daily_digest: "Guide",
    journeydailystep: "Guide",
    "journey-daily-step": "Guide",
    meditation: "Meditation",
    meditations: "Meditation",
    soundscape: "Soundscape",
    soundscapes: "Soundscape",
    bedtimestory: "BedtimeStory",
    "bedtime-story": "BedtimeStory",
    bedtime_story: "BedtimeStory",
    "bedtime story": "BedtimeStory",
};
export function isLibraryKind(value) {
    return typeof value === "string" && LIBRARY_KINDS.includes(value);
}
export function isShareContentType(value) {
    return isLibraryKind(value);
}
function record(kind) {
    return KIND_RECORDS[kind];
}
export function playbackClass(kind) {
    if (kind === "Microstep")
        return "read";
    return record(kind).playback;
}
export function collectionTabForKind(kind) {
    return record(kind).collectionTab;
}
export function showsPlayControl(kind) {
    if (kind === "reset" ||
        kind === "guide" ||
        kind === "podcast" ||
        kind === "meditation" ||
        kind === "soundscape" ||
        kind === "bedtimeStory") {
        return true;
    }
    if (kind === "article" || kind === "roleModel" || kind === "recipe")
        return false;
    const playback = playbackClass(kind);
    return playback === "video" || playback === "audio";
}
export function appearsOnPlanToday(kind) {
    return record(kind).planToday;
}
export function planJsonbSlot(kind) {
    return record(kind).planJsonbSlot;
}
export function displayLibraryKind(kind) {
    return record(kind).displayKind;
}
export function accessPolicyFamily(kind) {
    if (isLibraryKind(kind))
        return record(kind).accessPolicyFamily;
    return "Story";
}
export function contentKindOpenCta(kind) {
    if (isLibraryKind(kind))
        return record(kind).openCta;
    return "Open HealthOS";
}
export function reminderKindForContent(kind) {
    if (!isLibraryKind(kind))
        return null;
    return record(kind).reminderKind;
}
function resolvedLibraryKind(kind) {
    if (isLibraryKind(kind))
        return kind;
    return MEMBER_KIND_ALIASES[kind.trim().toLowerCase()] ?? null;
}
export function contentKindTitleLabel(kind) {
    const resolved = resolvedLibraryKind(kind);
    if (resolved)
        return record(resolved).titleLabel;
    if (kind === "Microstep")
        return "Microstep";
    return kind;
}
/**
 * Player and plan-card duration line. Guides are often shorter than a minute
 * and frequently have no stored length, so they never inherit the Reset
 * default of 60 seconds. A known length is shown; otherwise "short guide".
 */
export function videoDurationEyebrow(kind, durationSeconds) {
    const sentence = contentKindSentenceLabel(kind);
    // Meditations run several minutes, so they are counted in minutes.
    if (kind === "Meditation") {
        if (typeof durationSeconds === "number" && durationSeconds > 0) {
            return `${Math.max(1, Math.round(durationSeconds / 60))}-minute ${sentence}`;
        }
        return sentence;
    }
    if (kind === "Guide") {
        if (typeof durationSeconds === "number" && durationSeconds > 0) {
            return `${durationSeconds}-second ${sentence}`;
        }
        return `short ${sentence}`;
    }
    const seconds = typeof durationSeconds === "number" && durationSeconds > 0 ? durationSeconds : 60;
    return `${seconds}-second ${sentence}`;
}
export function contentKindSentenceLabel(kind) {
    const resolved = resolvedLibraryKind(kind);
    if (resolved)
        return record(resolved).sentenceLabel;
    return kind.trim().toLowerCase() || "content";
}
export function contentKindPhrase(kind) {
    const resolved = resolvedLibraryKind(kind);
    if (resolved) {
        const row = record(resolved);
        return { article: row.article, label: row.sentenceLabel };
    }
    const label = kind.trim().toLowerCase() || "content";
    return { article: label === "article" ? "an" : "a", label };
}
export function collectionTabTitle(tab) {
    const match = LIBRARY_KINDS.map(record).find((row) => row.collectionTab === tab);
    return match?.tabTitle ?? "Articles";
}
export const COLLECTION_TABS = COLLECTION_TAB_ORDER.map((id) => ({
    id,
    label: collectionTabTitle(id),
}));
export const COLLECTION_FILL_KINDS = LIBRARY_KINDS.filter((kind) => displayLibraryKind(kind) === kind);
export const RECOMMENDATION_CONTENT_TYPES = COLLECTION_FILL_KINDS.filter((kind) => record(kind).recommended);
export const CMS_INDEX_TYPES = [
    "Recipe",
    "Article",
    "RoleModel",
    "Reset",
    "Podcast",
    "PodcastEpisode",
    "Guide",
    "Meditation",
    "Soundscape",
    "BedtimeStory",
];
export function indexedKindFromCmsType(raw) {
    if (!raw)
        return undefined;
    return CMS_INDEX_ALIASES[raw.trim().toLowerCase()] ?? raw;
}
export function libraryKindFromMemberInput(raw) {
    if (!raw)
        return null;
    return resolvedLibraryKind(raw.trim());
}
/** Content index type → Collection kind. Podcast shows are not library items. Story shelves as Article. */
export function libraryKindFromIndexedType(type) {
    if (type === "Podcast")
        return null;
    if (type === "Story")
        return "Article";
    if (isLibraryKind(type))
        return type;
    return libraryKindFromMemberInput(type);
}
export function playbackDestinationForKind(kind) {
    if (kind === "Microstep")
        return "sheet";
    const playback = playbackClass(kind);
    if (playback === "video")
        return "video";
    if (playback === "audio")
        return "audio";
    return "sheet";
}
export function planItemId(kind, cmsUuid) {
    return `${kind.toLowerCase()}:${cmsUuid}`;
}
export function contentIdPrefixes() {
    const fromKinds = [...PLAN_ITEM_KINDS, ...LIBRARY_KINDS].map((kind) => kind.toLowerCase());
    return [...new Set([...fromKinds, "podcast", "role-model"])];
}
export function stripContentIdPrefix(rawId) {
    const prefixes = contentIdPrefixes().join("|");
    return rawId.replace(new RegExp(`^(${prefixes}):`, "i"), "");
}
export function isPlayableContent(kind, media) {
    const libraryKind = isLibraryKind(kind) ? kind : libraryKindFromIndexedType(kind);
    if (!libraryKind)
        return true;
    const playback = playbackClass(libraryKind);
    if (playback === "audio") {
        return Boolean(media.audioUrl || media.mediaUrl);
    }
    if (playback === "video") {
        return Boolean(media.videoUrl || media.mediaUrl);
    }
    return true;
}
export function libraryKindFromTitle(title) {
    const lower = title?.toLowerCase() ?? "";
    if (lower.includes("bedtime story"))
        return "BedtimeStory";
    if (lower.includes("meditation"))
        return "Meditation";
    if (lower.includes("soundscape"))
        return "Soundscape";
    if (lower.includes("recipe"))
        return "Recipe";
    if (lower.includes("role model"))
        return "RoleModel";
    if (lower.includes("podcast"))
        return "PodcastEpisode";
    if (lower.includes("guide"))
        return "Guide";
    if (lower.includes("story"))
        return "Story";
    if (lower.includes("reset"))
        return "Reset";
    if (lower.includes("article"))
        return "Article";
    return null;
}
//# sourceMappingURL=content-kind.js.map